import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Heart } from "lucide-react";
import { formatINR, useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import QuickViewDialog from "./QuickViewDialog";
import SafeImg from "./SafeImg";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [quickOpen, setQuickOpen] = useState(false);
  const liked = has(product.id);

  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, { size: "M", quantity: 1 });
    toast.success(`${product.name} added to cart`, {
      description: `Size M · ${formatINR(product.price)}`,
    });
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product);
    toast.success(liked ? "Removed from wishlist" : "Added to wishlist", {
      description: product.name,
    });
  };

  return (
    <>
      <article
        data-testid={`product-card-${product.id}`}
        className="group relative"
        style={{ animationDelay: `${Math.min(index, 6) * 80}ms` }}
      >
        <div className="relative overflow-hidden rounded-2xl bg-cream-50 aspect-[4/5] border border-blush-100/60 shadow-sm">
          <Link to={`/product/${product.id}`} className="block h-full w-full">
            <SafeImg
              src={product.images[0]}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
            />
            <SafeImg
              src={product.images[1] || product.images[0]}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            />
          </Link>

          {/* Top-left: status badge only (smaller, less obtrusive) */}
          {product.badge && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-[9px] uppercase tracking-[0.16em] font-body text-blush-700 border border-blush-100 shadow-sm">
              {product.badge}
            </span>
          )}

          {/* Bottom-left: discount chip (out of the model's face zone) */}
          {discount > 0 && (
            <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-blush-600 text-white text-[10px] uppercase tracking-[0.16em] font-body shadow-sm">
              -{discount}%
            </span>
          )}

          <button
            data-testid={`product-wishlist-${product.id}`}
            onClick={handleWishlist}
            className={`absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center bg-white/90 backdrop-blur border border-blush-100 transition ${
              liked ? "text-blush-600" : "text-ink-700 hover:text-blush-600"
            }`}
            aria-label="Wishlist"
          >
            <Heart size={15} strokeWidth={1.5} fill={liked ? "currentColor" : "none"} />
          </button>

          {/* Desktop-only quick actions — hidden on mobile so a single tap anywhere opens the product */}
          <div className="hidden md:flex absolute inset-x-0 bottom-0 px-3 pb-3 items-center justify-end gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
            <button
              data-testid={`product-quickview-${product.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickOpen(true); }}
              className="h-9 w-9 rounded-full bg-white/95 backdrop-blur border border-blush-100 grid place-items-center text-ink-700 hover:text-blush-600 hover:bg-blush-50 transition shadow-sm"
              aria-label="Quick view"
            >
              <Eye size={14} strokeWidth={1.6} />
            </button>
            <button
              data-testid={`product-quickadd-${product.id}`}
              onClick={handleQuickAdd}
              className="h-9 px-4 rounded-full bg-blush-600 text-white text-[11px] uppercase tracking-[0.2em] font-body hover:bg-blush-700 transition shadow-sm"
            >
              Add
            </button>
          </div>
        </div>

        <Link to={`/product/${product.id}`} className="block mt-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg text-ink-900 leading-snug">
                {product.name}
              </h3>
            </div>
            <div className="text-right">
              <p className="font-body text-ink-900">{formatINR(product.price)}</p>
              {product.originalPrice && (
                <p className="text-xs font-body text-ink-500 line-through">
                  {formatINR(product.originalPrice)}
                </p>
              )}
            </div>
          </div>
        </Link>
      </article>

      <QuickViewDialog
        product={product}
        open={quickOpen}
        onOpenChange={setQuickOpen}
      />
    </>
  );
}
